// Inline image renderer for the AI home. Calls hub-image, shows a
// skeleton while the model paints, then a clickable image with a
// prominent Download button (the whole point of this surface).
import { useEffect, useRef, useState } from "react";
import { ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { promptToFilename, saveImage } from "@/lib/downloads";
import { cn } from "@/lib/utils";
import ResultActions from "./ResultActions";

type Status = "loading" | "done" | "error";

export type ImageResultState = {
  prompt: string;
  imageUrl: string | null;
  status: Status;
  error?: string | null;
  /** Optional input image (data URL) when editing/remixing instead of pure generation. */
  sourceImageUrl?: string | null;
};

type Props = {
  state: ImageResultState;
  onPatch: (patch: Partial<ImageResultState>) => void;
  onRegenerate: () => void;
  onUseInChat?: (text: string) => void;
};

export default function ImageResult({ state, onPatch, onRegenerate, onUseInChat }: Props) {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || state.status !== "loading" || state.imageUrl) return;
    fired.current = true;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("hub-image", {
          body: {
            prompt: state.prompt,
            ...(state.sourceImageUrl ? { imageUrl: state.sourceImageUrl } : {}),
          },
        });
        if (error) throw new Error(error.message);
        const url = (data as any)?.image as string | undefined;
        if (!url) throw new Error("No image returned");
        onPatch({ imageUrl: url, status: "done" });
      } catch (e: any) {
        const msg = e?.message ?? "Image generation failed";
        onPatch({ status: "error", error: msg });
        toast.error(msg);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fname = `${promptToFilename(state.prompt)}.png`;

  return (
    <div className="space-y-2">
      <div
        className={cn(
          "relative aspect-square w-full max-w-sm rounded-2xl overflow-hidden border border-border bg-card/60",
          state.status === "loading" && "animate-pulse",
        )}
      >
        {state.imageUrl ? (
          <img
            src={state.imageUrl}
            alt={state.prompt}
            className="w-full h-full object-cover"
          />
        ) : state.status === "error" ? (
          <div className="absolute inset-0 grid place-items-center text-center px-4">
            <div>
              <ImageIcon className="w-6 h-6 mx-auto text-destructive mb-2" />
              <p className="text-xs text-destructive font-mono">{state.error}</p>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center">
              <Loader2 className="w-6 h-6 mx-auto text-primary animate-spin mb-2" />
              <p className="text-[11px] font-mono text-muted-foreground">Painting…</p>
            </div>
          </div>
        )}
      </div>
      <ResultActions
        copyText={state.prompt}
        onDownload={
          state.imageUrl
            ? () => saveImage(state.imageUrl!, fname)
            : undefined
        }
        downloadLabel="Download .png"
        onRegenerate={state.status !== "loading" ? onRegenerate : undefined}
        rightSlot={
          state.imageUrl && onUseInChat ? (
            <button
              type="button"
              onClick={() => onUseInChat(state.prompt)}
              className="h-7 px-2 inline-flex items-center gap-1 rounded-md text-[10px] font-mono text-muted-foreground hover:text-foreground hover:bg-secondary/60"
            >
              Use prompt
            </button>
          ) : null
        }
      />
    </div>
  );
}